import { orderBy, SortDescriptor } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Grid, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { HeaderComponent } from 'components/header/component'
import { Loading } from 'components/loading'
import { format } from 'date-fns'
import { Dataset } from 'generic/dataset'
import * as React from 'react'
import { formatDateForES } from 'shared_server_client/dates'
import * as Constants from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'

interface NewsletterSummaryRecord {
  identifier: string
  name: string
  issue_id: number | string
  send_at: string
  status: string
  num_sent: number
  expected: number
  first_generated: string
  last_generated: string
  num_late: number
  minutes_delayed: number | null
}

export class NewslettersSummaryComponent extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props, {
      data: [] as NewsletterSummaryRecord[],
      isLoading: false,
      selectedDate: new Date(),
      sort: [] as SortDescriptor[],
    })
  }

  public componentDidMount() {
    super.componentDidMount()
    this.context.user.init().then(({ activeCommunity }) => {
      this.setState({ communityId: activeCommunity.communityId }, () => this.loadData())
    })
  }

  public render() {
    return (
      <div className="analytics-component">
        <HeaderComponent
          title="ANALYTICS"
          subTitle="Newsletters Summary"
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DatePicker
              value={this.state.selectedDate}
              onChange={this.handleDateChange}
              max={new Date()}
              format="yyyy-MM-dd"
            />
          </div>
        </div>
        <div className="date-range-picker" />
        {this.renderContent()}
      </div>
    )
  }

  private renderContent = () => {
    if (this.state.isLoading) {
      return (
        <div className="analytics-loading">
          <Loading size="32" />
        </div>
      )
    }

    if (!this.state.data || !this.state.data.length) {
      return <p className="no-data-tag">{Constants.NO_DATA_COPY}</p>
    }

    return (
      <Grid
        data={orderBy(this.state.data, this.state.sort)}
        sortable={true}
        sort={this.state.sort}
        onSortChange={(e: GridSortChangeEvent) => this.setState({ sort: e.sort })}
        className="analytics-counts-grid"
      >
        <GridColumn field="identifier" title="Identifier" width={150} />
        <GridColumn field="name" title="Name" width={200} />
        <GridColumn field="issue_id" title="Issue ID" width={100} />
        <GridColumn field="send_at" title="Send At" width={110} cell={this.formatDateCell('send_at')} />
        <GridColumn field="status" title="Status" width={110} />
        <GridColumn field="expected" title="Expected" width={110} />
        <GridColumn field="num_sent" title="Sent" width={80} />
        <GridColumn field="num_late" title="Late" width={80} />
        <GridColumn field="minutes_delayed" title="Delayed (min)" width={130} />
        <GridColumn field="first_generated" title="First Send" width={130} cell={this.formatDateCell('first_generated')} />
        <GridColumn field="last_generated" title="Last Send" width={130} cell={this.formatDateCell('last_generated')} />
      </Grid>
    )
  }

  private handleDateChange = (e) => {
    if (!e.value) return
    this.setState({ selectedDate: e.value }, () => this.loadData())
  }

  private formatDateCell = (field: string) => (props: any) => {
    const value = props.dataItem[field]
    if (!value) return <td></td>
    try {
      return <td>{format(new Date(value), 'MMM dd, yyyy HH:mm')}</td>
    } catch {
      return <td>{value}</td>
    }
  }

  private loadData = () => {
    if (!this.state.communityId) return
    this.setState({ isLoading: true })
    const params = [
      { param: 'date', value: formatDateForES(this.state.selectedDate) },
    ]
    return new Dataset()
      .loadCommunityDataset('newslettersSummary', this.state.communityId, params)
      .then((response) => {
        this.setState({
          data: response[0] || [],
          isLoading: false,
        })
      })
      .catch(() => {
        this.setState({ isLoading: false })
      })
  }
}
